import { Formik } from 'formik';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { MediumButton, MediumPrimaryButton, OutlineButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import { FormField, FormFieldContainer, Label } from '../../../components/FormElements';
import { ModalFooter } from '../../../components/ModalElements';
import Overlay from '../../../components/Overlay';
import { RichTextEditor } from '../../../components/richTextEditor';
import { useUser } from '../../authentication';
import { Content } from '../models/content';
import { CourseStep } from '../models/course';
import { StepType } from '../models/stepType';
import { createStep, getValidationSchema, stepTypeOptions } from '../utils/courseStepUtils';
import { ContentStep } from './ContentStep';
import { VideoPreview } from './VideoPreview';

const EditStepContainer = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const OverlayContainer = styled.div`
  width: 100vw;
  padding: ${Grid._6};

  h3 {
    font-size: ${Type.Scale._5};
    font-weight: ${Type.Weight.bold};
    margin-top: 0;
    margin-bottom: ${Grid._4};
    color: ${Color.Gray._90};
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    width: 85vw;
    max-width: ${Breakpoints.screen_md};
  }
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${Grid._4};
  margin-bottom: ${Grid._2};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const IconWrapper = styled.span`
  margin-right: ${Grid._2};
  font-size: ${Type.Scale._2};
`;

type AddCourseStepModalProps = {
  courseId: string;
  stepOrder: number;
  handleDismiss: () => void;
  onSuccess?: (step: CourseStep) => void | Promise<void>;
};

type CurrentStep = 'select' | 'form' | 'preview' | 'upload';

const getInitialValues = (
  selectedStepType: StepType
): {
  stepType: StepType;
  name: string;
  description: string;
  url?: string;
  contentId?: string;
  selectedContents: Content[];
} => {
  const base = {
    stepType: (selectedStepType || 'text') as StepType,
    name: '',
    description: '',
    selectedContents: [] as Content[],
  };

  if (selectedStepType === 'link' || selectedStepType === 'youtube' || selectedStepType === 'vimeo') {
    return {
      ...base,
      url: '',
    };
  }

  if (
    selectedStepType === 'video' ||
    selectedStepType === 'download' ||
    selectedStepType === 'assessment' ||
    selectedStepType === 'audio' ||
    selectedStepType === 'documentViewer'
  ) {
    return {
      ...base,
      contentId: '',
    };
  }

  return base;
};

export const AddCourseStepModal: React.FC<AddCourseStepModalProps> = ({
  courseId,
  stepOrder,
  handleDismiss,
  onSuccess = () => Promise.resolve(),
}) => {
  const { user } = useUser();
  const [currentStep, setCurrentStep] = useState<CurrentStep>('select');
  const [selectedStepType, setSelectedStepType] = useState<StepType | ''>('');
  const [contentSearchQuery, setContentSearchQuery] = useState('');
  const [previewContent, setPreviewContent] = useState<{ name: string; id: string }>();
  const [error, setError] = useState(false);

  const handleSubmit = (values: {
    stepType: StepType;
    name: string;
    description: string;
    url?: string;
    contentId?: string;
    selectedContents: Content[];
  }) => {
    if (!user?.userId) {
      return;
    }

    return createStep({
      courseId,
      stepOrder,
      userId: user.userId,
      values,
    })
      .then((steps: CourseStep | CourseStep[]) => {
        if (Array.isArray(steps)) {
          steps.forEach(step => onSuccess(step));
        } else {
          onSuccess(steps);
        }
        handleDismiss();
      })
      .catch(() => {
        setError(true);
      });
  };

  const renderFormFields = (
    setFieldValue: (field: string, value: string | Content[]) => void,
    values: {
      stepType: StepType;
      name: string;
      description: string;
      url?: string;
      contentId?: string;
      selectedContents: Content[];
    }
  ) => {
    if (!selectedStepType) {
      return null;
    }

    const commonFields = (
      <>
        <FormField label="Name" name="name" type="text" placeholder="Enter step name" />
        <FormFieldContainer>
          <Label htmlFor="description">Description</Label>
          <RichTextEditor
            value={values.description || ''}
            onChange={newValue => setFieldValue('description', newValue)}
            allowedTags={['strong', 'em', 'u', 'ul', 'ol', 'li', 'p', 'br', 'a', 'img']}
            placeholder="Enter step description"
          />
        </FormFieldContainer>
      </>
    );

    if (selectedStepType === 'link' || selectedStepType === 'youtube' || selectedStepType === 'vimeo') {
      return (
        <>
          {commonFields}

          <FormField label="URL" name="url" type="url" placeholder="Enter URL" />
          {(selectedStepType === 'youtube' || selectedStepType === 'vimeo') && <VideoPreview url={values.url || ''} />}
        </>
      );
    }

    if (selectedStepType === 'text' || selectedStepType === 'upload') {
      return commonFields;
    }

    if (
      selectedStepType === 'assessment' ||
      selectedStepType === 'video' ||
      selectedStepType === 'audio' ||
      selectedStepType === 'download' ||
      selectedStepType === 'documentViewer'
    ) {
      return (
        <ContentStep
          selectedContents={values.selectedContents}
          contentSearchQuery={contentSearchQuery}
          setSelectedContents={(contents: Content[]) => {
            setFieldValue('selectedContents', contents);
            setFieldValue('contentId', contents.length > 0 ? contents[0].id : '');
          }}
          setContentSearchQuery={setContentSearchQuery}
          stepType={selectedStepType}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          previewContent={previewContent}
          setPreviewContent={setPreviewContent}
          onUploadComplete={(content: Content) => {
            const newValues = { ...values, selectedContents: [content], contentId: content.id };
            handleSubmit(newValues);
          }}
        />
      );
    }

    return null;
  };

  const handleTypeSelect = (type: StepType, setFieldValue: (field: string, value: string | Content[]) => void) => {
    setSelectedStepType(type);
    setCurrentStep('form');
    setContentSearchQuery('');
    if (type !== 'video') {
      setFieldValue('selectedContents', []);
      setFieldValue('contentId', '');
    }
  };

  const handleBack = (setFieldValue?: (field: string, value: string | Content[]) => void) => {
    if (currentStep === 'preview') {
      setCurrentStep('form');
      setPreviewContent(undefined);
      return;
    }
    if (currentStep === 'upload') {
      setCurrentStep('form');
      return;
    }
    if (currentStep === 'form') {
      setCurrentStep('select');
      setSelectedStepType('');
      setContentSearchQuery('');
      if (setFieldValue) {
        setFieldValue('selectedContents', []);
        setFieldValue('contentId', '');
      }
      return;
    }
  };

  const getHeaderText = () => {
    if (currentStep === 'select') {
      return 'Add Step';
    }
    if (currentStep === 'preview') {
      return 'Preview';
    }
    if (currentStep === 'upload') {
      return 'Upload File';
    }
    if (selectedStepType) {
      const stepTypeLabel = stepTypeOptions.find(option => option.value === selectedStepType)?.label || 'Step';
      return `Add ${stepTypeLabel} Step`;
    }
    return 'Add Step';
  };

  const renderStepOne = (setFieldValue: (field: string, value: string | Content[]) => void) => (
    <>
      <p>Select a step type you want to add</p>
      <TypeGrid>
        {stepTypeOptions.map(option => (
          <OutlineButton key={option.value} onClick={() => handleTypeSelect(option.value as StepType, setFieldValue)}>
            <IconWrapper>
              <i className={option.icon}></i>
            </IconWrapper>
            {option.label}
          </OutlineButton>
        ))}
      </TypeGrid>
    </>
  );

  return (
    <Overlay dismissHandler={handleDismiss} disableBackgroundDismiss>
      <>
        <Overlay.CloseButton />

        <Formik
          initialValues={getInitialValues(selectedStepType as StepType)}
          validationSchema={getValidationSchema(selectedStepType as StepType)}
          enableReinitialize
          onSubmit={values => handleSubmit(values)}
        >
          {({ isSubmitting, setFieldValue, handleSubmit: formikSubmit, values }) => (
            <EditStepContainer onSubmit={formikSubmit}>
              <OverlayContainer>
                <h3>{getHeaderText()}</h3>
                {currentStep === 'select' ? (
                  renderStepOne(setFieldValue)
                ) : (
                  <>{renderFormFields(setFieldValue, values)}</>
                )}
                {error && <ErrorMessage>An error occurred while saving the step. Please try again.</ErrorMessage>}
              </OverlayContainer>
              {currentStep !== 'select' && (
                <ModalFooter>
                  <MediumButton type="button" onClick={() => handleBack(setFieldValue)}>
                    Back
                  </MediumButton>
                  {currentStep === 'form' && (
                    <MediumPrimaryButton
                      onClick={formikSubmit}
                      type="submit"
                      disabled={isSubmitting}
                      operating={isSubmitting}
                    >
                      Add Step
                    </MediumPrimaryButton>
                  )}
                </ModalFooter>
              )}
            </EditStepContainer>
          )}
        </Formik>
      </>
    </Overlay>
  );
};
